'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { getFlipsterEarnBufferCardRow } from '@flipster/features/src/flipster-earn/sunset/display-conditions';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import {
  earnStatisticsBunja,
  earnSummaryBunja,
  getEarnSummaryOfCurrency,
} from '@flipster/features/src/shared/earn/state';
import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { convertDate } from '@flipster/utils/datetime';
import { isEarnSunsetProductCurrency } from '@flipster/utils/earn-sunset';

import { earnPageBunja } from '../../state';
import { ActiveAprCard } from './active-apr-card';
import { InactiveAprCard } from './inactive-apr-card';
import { LatestUpdate } from './latest-update';

export const BufferEarnAprSection = () => {
  const { isLoggedInAtom } = useBunja(authBunja);
  const { hasFirstDepositAtom } = useBunja(earnPageBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const { earnStatisticsAtom } = useBunja(earnStatisticsBunja);

  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const hasFirstDeposit = useAtomValue(hasFirstDepositAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const earnStatistics = useAtomValue(earnStatisticsAtom);
  const latestUpdate = convertDate(earnStatistics?.aprUpdatedAt);

  const currencies = earnSummary?.currencies ?? [];
  const rows = currencies.map((c) => {
    const isSunsetProduct = isEarnSunsetProductCurrency(c.currency);
    const { isMintAvailable } = getEarnSummaryOfCurrency(c.currency, earnSummary);
    const row = isSunsetProduct
      ? getFlipsterEarnBufferCardRow({ isLoggedIn, hasFirstDeposit, isMintAvailable })
      : isLoggedIn && hasFirstDeposit
        ? 2
        : 1;
    return { currency: c.currency, row };
  });

  const hasActiveRow = rows.some((item) => item.row === 2);

  return (
    <section className={stack({ gap: 20 })}>
      {hasActiveRow ? (
        <div className={css({ textStyle: 'Title/M', color: 'content.default.level-5' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Your_earnings' />
        </div>
      ) : (
        <div className={hstack({ justify: 'space-between' })}>
          <div className={stack({ gap: 4 })}>
            <p className={css({ textStyle: 'Title/M', color: 'content.default.level-5' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Deposit_and_start_earning' />
            </p>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Earn_and_trade_seamlessly_Grow_your_balance_while_you_trade' />
            </p>
          </div>
        </div>
      )}
      <div className={hasActiveRow ? stack({ gap: 10 }) : vstack({ alignItems: 'stretch', gap: 10 })}>
        {rows.map(({ currency, row }) =>
          row === 2 ? (
            <ActiveAprCard key={currency} currency={currency} />
          ) : (
            <InactiveAprCard key={currency} currency={currency} addDisabled={row === 3} />
          ),
        )}
      </div>
      {hasActiveRow && <LatestUpdate date={latestUpdate} />}
    </section>
  );
};
